import CoreAnalytics from './coreAnalytics';

class NoteAuditorAnalytics extends CoreAnalytics {
  EVENT_NAMES = {
    LEARNING_CREATION_STARTED: 'learning_creation_started',
    LEARNING_EDIT_OPENED: 'learning_edit_opened',
    LEARNING_SAVED: 'learning_saved',
    LEARNING_VALIDATION_COMPLETED: 'learning_validation_completed',
    LEARNING_EDIT_ABANDONED: 'learning_edit_abandoned',
    LEARNING_REVISION_RESTORED: 'learning_revision_restored',
    LEARNING_DELETED: 'learning_deleted',
    LEARNING_STATUS_CHANGED: 'learning_status_changed',
  };

  LEARNING_EDIT_MODES = {
    CREATE: 'create',
    EDIT: 'edit',
  };

  LEARNING_SAVE_INTENTS = {
    VERIFIED: 'verified',
    DRAFT: 'draft',
  };

  LEARNING_SCOPES = {
    ORGANIZATION: 'organization',
    TEMPLATES: 'templates',
  };

  LEARNING_STATUSES = {
    ACTIVE: 'active',
    ACTIVE_WITH_DRAFT: 'active_with_draft',
    DRAFT: 'draft',
    REVISION_DRAFT: 'revision_draft',
  };

  LEARNING_DELETE_VARIANTS = {
    ACTIVE_NO_DRAFT: 'active_no_draft',
    ACTIVE_WITH_DRAFT: 'active_with_draft',
    STANDALONE_DRAFT: 'standalone_draft',
    REVISION_DRAFT: 'revision_draft',
  };

  LEARNING_VALIDATION_OUTCOMES = {
    PASSED: 'passed',
    FAILED: 'failed',
  };

  LEARNING_SCOPE_CHANGES = {
    TO_ORGANIZATION: 'templates_to_organization',
    TO_TEMPLATES: 'organization_to_templates',
    TEMPLATES_CHANGED: 'templates_changed',
  };

  LEARNING_STATUS_CHANGE_CAUSES = {
    VERIFIED_SAVE: 'verified_save',
    REVISION_PROMOTED: 'revision_promoted',
    PARENT_DELETED: 'parent_deleted',
  };

  /**
   * @param {Object} params
   * @param {string} params.learningEditAttemptId - ID of a single open-to-close pass over the modal.
   * @param {number|null} [params.totalLearnings] - Learnings the clinic already has.
   * @returns {void}
   */
  trackLearningCreationStarted({ learningEditAttemptId, totalLearnings = null }) {
    if (!learningEditAttemptId) {
      return;
    }

    this.track(this.EVENT_NAMES.LEARNING_CREATION_STARTED, {
      learningEditAttemptId,
      totalLearnings,
      eventVersion: 1,
    });
  }

  /**
   * @param {Object} params
   * @param {string} params.learningEditAttemptId
   * @param {string} params.learningId
   * @param {string} params.source - Where the learning came from: manual or feedback.
   * @param {string} params.status - The learning's status as the table shows it.
   * @param {string} params.scope - Organization or specific templates.
   * @param {number} params.templateCount - Templates it is scoped to; 0 for organization scope.
   * @returns {void}
   */
  trackLearningEditOpened({
    learningEditAttemptId,
    learningId,
    source,
    status,
    scope,
    templateCount = 0,
  }) {
    if (!learningEditAttemptId || !learningId) {
      return;
    }

    this.track(this.EVENT_NAMES.LEARNING_EDIT_OPENED, {
      learningEditAttemptId,
      learningId,
      source,
      status,
      scope,
      templateCount,
      eventVersion: 1,
    });
  }

  /**
   * @param {Object} params
   * @param {string} params.learningEditAttemptId
   * @param {string} params.mode - create or edit.
   * @param {string} params.saveIntent - verified or draft.
   * @param {string} params.scope
   * @param {number} params.templateCount
   * @param {string|null} [params.learningId] - Absent on a create, which has no id until it is written.
   * @param {string|null} [params.source] - Edits only: where the learning came from.
   * @param {string|null} [params.previousStatus] - Edits only: the status before this save.
   * @param {boolean} [params.revisionCreated] - Whether this save parked a revision beside an Active one.
   * @param {boolean} [params.revisionPromoted] - Whether this save replaced an Active one with its revision.
   * @param {boolean} [params.scopeChanged] - Edits only.
   * @param {string|null} [params.scopeChange] - Which way the scope moved, when it moved.
   * @param {boolean} [params.restoreUsed] - Whether the manager pulled the replaced wording back first.
   * @param {number} [params.validationFailureCount] - Rejections survived before this save landed.
   * @returns {void}
   */
  trackLearningSaved({
    learningEditAttemptId,
    mode,
    saveIntent,
    scope,
    templateCount = 0,
    learningId = null,
    source = null,
    previousStatus = null,
    revisionCreated = false,
    revisionPromoted = false,
    scopeChanged = false,
    scopeChange = null,
    restoreUsed = false,
    validationFailureCount = 0,
  }) {
    if (!learningEditAttemptId || !mode || !saveIntent || !scope) {
      return;
    }

    this.track(this.EVENT_NAMES.LEARNING_SAVED, {
      learningEditAttemptId,
      mode,
      saveIntent,
      scope,
      templateCount,
      learningId,
      source,
      previousStatus,
      revisionCreated,
      revisionPromoted,
      scopeChanged,
      scopeChange,
      restoreUsed,
      validationFailureCount,
      eventVersion: 1,
    });
  }

  /**
   * @param {Object} params
   * @param {string} params.learningEditAttemptId
   * @param {string} params.mode
   * @param {string} params.outcome - passed or failed.
   * @param {string|null} [params.failureReason] - Category of the rejection; null when it passed.
   * @param {number} [params.attemptNumber] - Which validation attempt this was within the pass.
   * @param {string|null} [params.learningId]
   * @returns {void}
   */
  trackLearningValidationCompleted({
    learningEditAttemptId,
    mode,
    outcome,
    failureReason = null,
    attemptNumber = 1,
    learningId = null,
  }) {
    if (!learningEditAttemptId || !mode || !outcome) {
      return;
    }

    this.track(this.EVENT_NAMES.LEARNING_VALIDATION_COMPLETED, {
      learningEditAttemptId,
      mode,
      outcome,
      failureReason,
      attemptNumber,
      learningId,
      eventVersion: 1,
    });
  }

  /**
   * @param {Object} params
   * @param {string} params.learningEditAttemptId
   * @param {string} params.mode
   * @param {number} [params.validationFailureCount]
   * @param {boolean} [params.hadChanges] - Whether anything was typed or picked before closing.
   * @returns {void}
   */
  trackLearningEditAbandoned({
    learningEditAttemptId,
    mode,
    validationFailureCount = 0,
    hadChanges = false,
  }) {
    if (!learningEditAttemptId || !mode) {
      return;
    }

    this.track(this.EVENT_NAMES.LEARNING_EDIT_ABANDONED, {
      learningEditAttemptId,
      mode,
      validationFailureCount,
      hadChanges,
      eventVersion: 1,
    });
  }

  /**
   * @param {Object} params
   * @param {string} params.learningEditAttemptId
   * @param {string} params.learningId - The revision draft being edited.
   * @returns {void}
   */
  trackLearningRevisionRestored({ learningEditAttemptId, learningId }) {
    if (!learningEditAttemptId || !learningId) {
      return;
    }

    this.track(this.EVENT_NAMES.LEARNING_REVISION_RESTORED, {
      learningEditAttemptId,
      learningId,
      eventVersion: 1,
    });
  }

  /**
   * @param {Object} params
   * @param {string} params.learningId
   * @param {string} params.variant
   * @param {string|null} [params.source]
   * @param {string|null} [params.scope]
   * @param {number} [params.templateCount]
   * @returns {void}
   */
  trackLearningDeleted({
    learningId,
    variant,
    source = null,
    scope = null,
    templateCount = 0,
  }) {
    if (!learningId || !variant) {
      return;
    }

    this.track(this.EVENT_NAMES.LEARNING_DELETED, {
      learningId,
      variant,
      source,
      scope,
      templateCount,
      eventVersion: 1,
    });
  }

  /**
   * @param {Object} params
   * @param {string} params.learningId
   * @param {string} params.fromStatus
   * @param {string} params.toStatus
   * @param {string} params.cause
   * @param {number|null} [params.draftAgeMinutes] - Age of the draft when it stopped being one.
   * @param {string|null} [params.learningEditAttemptId] - Present when a modal pass caused the change.
   * @returns {void}
   */
  trackLearningStatusChanged({
    learningId,
    fromStatus,
    toStatus,
    cause,
    draftAgeMinutes = null,
    learningEditAttemptId = null,
  }) {
    if (!learningId || !fromStatus || !toStatus || !cause) {
      return;
    }

    this.track(this.EVENT_NAMES.LEARNING_STATUS_CHANGED, {
      learningId,
      fromStatus,
      toStatus,
      cause,
      draftAgeMinutes,
      learningEditAttemptId,
      eventVersion: 1,
    });
  }
}

export default new NoteAuditorAnalytics();
